import React, { useCallback } from "react";
import { Paper, Typography, Box, Stack, useTheme } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Tooltip,
} from "recharts";
import CustomEmptyState from "global/components/CustomEmptyState/CustomEmptyState";
import ChartDataTable from "global/components/ChartDataTable/ChartDataTable";

interface Props {
  data: any;
  isMobile: boolean;
}

interface BarLabelProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  value?: any;
}
const TotalDocumentProgressGraph = ({ data, isMobile }: Props) => {
  const theme = useTheme();
  const { series } = theme.palette.chart;
  const labelColor = theme.palette.chart.label;

  const renderCustomBarLabel = useCallback(
    (props: BarLabelProps) => {
      const { x, y, width, value } = props;
      if (x === undefined || y === undefined || width === undefined)
        return null;
      return (
        <text
          x={Number(x) + Number(width) / 2}
          y={Number(y) - 10}
          fill={labelColor}
          textAnchor="middle"
          style={{ fontSize: "12px", fontWeight: 700 }}
        >
          {value.toLocaleString()}
        </text>
      );
    },
    [labelColor],
  );
  if (!data?.counts || !data?.percentages) return null;

  const { counts, percentages, closeRate } = data;

  const hasData =
    counts.requested > 0 ||
    counts.sent > 0 ||
    counts.opened > 0 ||
    counts.completed > 0;
  if (!hasData) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: isMobile ? 320 : 380,
        }}
      >
        <CustomEmptyState
          icon="report"
          title="No data available"
          description="No data available for the selected date range."
          isMobile={isMobile}
        />
      </Box>
    );
  }

  const chartData = [
    { name: "Sent", count: counts.sent, color: series.sent },
    { name: "Opened", count: counts.opened, color: series.opened },
    { name: "Completed", count: counts.completed, color: series.completed },
  ];

  const arrowPositions = chartData.slice(1).map((_, i) => ({
    percent: [percentages.openedPer, percentages.completedPer][i],
    left: `${((i + 1) / chartData.length) * 100}%`,
  }));

  // Stage-to-stage conversion is drawn only as an arrow with a percentage
  // floating over the plot; the table restates it as a value per stage.
  const conversionForStage = (index: number) => {
    if (index === 0) return "—";
    const percent = arrowPositions[index - 1]?.percent;
    return `${Number(percent || 0).toFixed(1)}%`;
  };

  /**
   * `role="img"` collapses the plot — bars, value labels and the conversion
   * arrows alike — into a single node, so the label has to say what all three
   * convey (WCAG 1.1.1).
   */
  const chartSummary =
    `Funnel chart of document progress. ` +
    chartData
      .map(
        (item, index) =>
          `${item.name} ${Number(item.count || 0).toLocaleString()}` +
          (index === 0 ? "" : ` (${conversionForStage(index)} conversion)`),
      )
      .join(", ") +
    `. Overall completion rate ${closeRate.toFixed(1)} percent.`;

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "visible",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        bgcolor: "transparent",
      }}
    >
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="flex-end"
        mb={3}
        spacing={1}
      >
        <Stack alignItems={"flex-end"}>
          <Typography
            sx={{
              fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.7rem" },
              lineHeight: 1,
              fontWeight: 800,
              color: "text.primary",
            }}
          >
            {closeRate.toFixed(1)}%
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: "uppercase" }}
          >
            completion rate
          </Typography>
        </Stack>
      </Stack>

      <Box
        role="img"
        aria-label={chartSummary}
        sx={{
          width: "100%",
          flexGrow: 1,
          minHeight: isMobile ? 250 : 263,
          height: isMobile ? 250 : 263,
          position: "relative",
          mt: 1,
        }}
      >
        {arrowPositions.map((item, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: "45%",
              left: item.left,
              transform: "translateX(-50%)",
              pointerEvents: "none",
              zIndex: 1,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "text.secondary",
                mb: -0.8,
              }}
            >
              {Number(item.percent || 0).toFixed(1)}%
            </Typography>
            <ArrowRightAltIcon sx={{ color: "chart.cursor", fontSize: 20 }} />
          </Box>
        ))}

        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            accessibilityLayer={false}
            data={chartData}
            margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
          >
            <XAxis
              dataKey="name"
              axisLine={true}
              tickLine={true}
              tick={{
                fontSize: isMobile ? 10 : 12,
                fontWeight: 600,
                fill: theme.palette.chart.axis,
              }}
              dy={10}
            />
            <YAxis hide domain={[0, (dataMax: number) => dataMax * 1.2]} />
            <Tooltip
              cursor={{ fill: theme.palette.action.hover }}
              wrapperStyle={{ zIndex: 1000 }}
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: theme.palette.chart.tooltipShadow,
              }}
            />

            <Bar
              dataKey="count"
              radius={[6, 6, 0, 0]}
              barSize={isMobile ? 32 : 50}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList
                dataKey="count"
                content={(BarLabelProps) => renderCustomBarLabel(BarLabelProps)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <ChartDataTable
        caption="Total document progress by stage"
        columns={["Stage", "Documents", "Conversion"]}
        rows={chartData.map((item, index) => [
          item.name,
          Number(item.count || 0).toLocaleString(),
          conversionForStage(index),
        ])}
      />

      <Stack
        direction="row"
        justifyContent="center"
        columnGap={isMobile ? 2 : 3}
        rowGap={1}
        sx={{ mt: 3, pb: 1, flexWrap: "wrap" }}
      >
        {chartData.map((item) => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            key={item.name}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: item.color,
                borderRadius: "3px",
              }}
            />
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {item.name}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export default TotalDocumentProgressGraph;
